import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Heart, User, LogOut, Menu, Search, Bell } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { productService } from '../services/productService';
import StoreSidebar from './StoreSidebar';
import AdminNotificationsBell from './AdminNotificationsBell';

const Header = ({ onSidebarFilter }) => {
  const { user, logout, isAdmin } = useAuth();
  const { cartCount, wishlist } = useCart();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await productService.getAllProducts();
        setProducts(response.data || []);
      } catch (error) {
        console.error('Failed to fetch products');
      }
    };
    if (user && !isAdmin) fetchProducts();
  }, [user, isAdmin]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const liveCategories = products.reduce((acc, p) => {
    if (p.category && !acc.includes(p.category)) acc.push(p.category);
    return acc;
  }, []);

  return (
    <header className="sticky top-0 z-50 flex flex-col w-full bg-white">
      <StoreSidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        dynamicCategories={liveCategories}
        products={products}
        onFilterChange={onSidebarFilter}
      />
      
      {/* Line 1: Info Bar */}
      <div className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white shrink-0">
        <div className="max-w-7xl mx-auto px-3 sm:px-4">
          <div className="flex items-center justify-between py-1.5 text-[11px] sm:text-sm">
            <div className="flex items-center divide-x divide-white/30">
              <span className="px-1 sm:px-3 truncate">📧 sales@ProNest.in</span>
              <span className="px-1 sm:px-3 truncate">📞 +91 7010929678</span>
            </div>
            <div className="flex items-center divide-x divide-white/30">
              <span className="px-1 sm:px-3 truncate">🚚 Free Shipping ₹500+</span>
              {user && <span className="px-1 sm:px-2 text-[10px] truncate max-w-[70px] sm:max-w-[120px]">👋 {user.name}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Line 2: Main Branding & Navigation Row */}
      <nav className="w-full border-b border-slate-200/70 bg-white/90 backdrop-blur-xl shrink-0">
        <div className="mx-auto max-w-7xl px-3 sm:px-4">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => setSidebarOpen(true)} className="p-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition active:scale-95 shadow-xs shrink-0" aria-label="Open Sidebar Menu">
                <Menu size={18} className="text-slate-700" />
              </button>
              <Link to={isAdmin ? '/admin' : '/store'} className="flex items-center gap-2 group">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white shadow-xs ring-1 ring-slate-200 transition-all duration-300 group-hover:scale-105">
                  <img src="/images/pronest_logo.png" alt="ProNest Logo" className="h-8 w-8 object-contain" />
                </div>
                <div>
                  <h1 className="text-sm font-bold tracking-tight text-slate-900 leading-tight">ProNest</h1>
                  <p className="hidden text-[9px] font-semibold uppercase tracking-[0.2em] text-slate-400 sm:block">Premium Electronics</p>
                </div>
              </Link>
            </div>
            {user && (
              <div className="flex items-center gap-2 sm:gap-3">
                {(isAdmin || user?.role === 'vendor') && (
                  <AdminNotificationsBell isAdmin={true} />
                )}
                {!isAdmin && (
                  <div className="relative">

                    {searchOpen ? (
                      <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-2.5 py-2 shadow-sm">
                        <Search size={15} className="text-slate-500" />
                        <input
                          type="text"
                          placeholder="Search products"
                          autoFocus
                          onChange={(e) => {
                            const term = e.target.value.toLowerCase();
                            if (term.trim()) {
                              const filtered = products.filter((p) => p.name.toLowerCase().includes(term) || p.category?.toLowerCase().includes(term));
                              setSearchResults(filtered.slice(0, 8));
                            } else {
                              setSearchResults([]);
                            }
                          }}
                          className="w-32 bg-transparent text-xs text-slate-700 outline-none sm:w-48"
                        />
                        <button onClick={() => { setSearchOpen(false); setSearchResults([]); }} className="rounded-lg p-0.5 text-slate-500 hover:bg-white text-xs">✕</button>
                      </div>
                    ) : (
                      <button onClick={() => setSearchOpen(true)} className="rounded-2xl border border-slate-200 bg-white p-2.5 shadow-sm transition hover:border-purple-300 hover:bg-purple-50">
                        <Search size={18} className="text-slate-600" />
                      </button>
                    )}
                    {searchOpen && searchResults.length > 0 && (
                      <div className="absolute right-0 top-full mt-2 w-72 max-h-64 overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-2xl sm:w-80">
                        {searchResults.map((product) => (
                          <button
                            key={product._id}
                            onClick={() => {
                              navigate(`/product/${product._id}`);
                              setSearchOpen(false);
                              setSearchResults([]);
                            }}
                            className="flex w-full items-center gap-3 border-b border-slate-100 p-3 text-left last:border-0 hover:bg-purple-50"
                          >
                            <div className="h-9 w-9 flex-shrink-0 rounded-xl overflow-hidden bg-slate-100">
                              <img src={product.image || 'https://via.placeholder.com/40'} alt={product.name} className="h-full w-full object-cover" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-xs font-medium text-slate-800">{product.name}</p>
                              <p className="text-[10px] text-slate-500">{product.category}</p>
                            </div>
                            <span className="text-xs font-semibold text-purple-600">₹{product.price}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {!isAdmin && (
                  <Link to="/wishlist" className="relative rounded-2xl border border-slate-200 bg-white p-2.5 shadow-sm transition hover:border-red-300 hover:bg-red-50">
                    <Heart size={18} className={wishlist.length ? 'fill-red-500 text-red-500' : 'text-slate-600'} />
                    {wishlist.length > 0 && <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">{wishlist.length}</span>}
                  </Link>
                )}
                {!isAdmin && (
                  <Link to="/cart" className="relative rounded-2xl border border-slate-200 bg-white p-2.5 shadow-sm transition hover:border-purple-300 hover:bg-purple-50">
                    <ShoppingCart size={18} className="text-slate-600" />
                    {cartCount > 0 && <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-purple-600 text-[9px] font-bold text-white">{cartCount}</span>}
                  </Link>
                )}
                <div className="hidden items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-1.5 sm:flex">
                  <User size={15} className="text-purple-600" />
                  <div>
                    <p className="text-xs font-semibold text-slate-700 leading-tight">{user.name}</p>
                    <p className="text-[10px] text-slate-500 leading-none">{user.role}</p>
                  </div>
                </div>
                <button onClick={handleLogout} className="hidden items-center gap-2 rounded-2xl bg-gradient-to-r from-purple-600 to-blue-600 px-3 py-2 text-xs font-medium text-white shadow-md shadow-purple-200 md:flex">
                  <LogOut size={15} />
                  <span>Logout</span>
                </button>
                <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="rounded-2xl border border-slate-200 bg-white p-2.5 shadow-sm md:hidden">
                  <Menu size={18} className="text-slate-600" />
                </button>
              </div>
            )}
          </div>
          {mobileMenuOpen && user && (
            <div className="space-y-1 border-t border-slate-200 bg-white py-3 text-xs md:hidden">
              <div className="mb-2 flex items-center justify-between px-2">
                <span className="font-semibold text-slate-700">Menu</span>
                <button onClick={() => setMobileMenuOpen(false)} className="rounded-lg p-1 hover:bg-slate-100">✕</button>
              </div>
              {!isAdmin && (
                <>
                  <Link to="/store" onClick={() => setMobileMenuOpen(false)} className="block rounded-xl px-2.5 py-2 text-slate-700 hover:bg-purple-50">Shop</Link>
                  <Link to="/wishlist" onClick={() => setMobileMenuOpen(false)} className="block rounded-xl px-2.5 py-2 text-slate-700 hover:bg-purple-50">Wishlist ({wishlist.length})</Link>
                  <Link to="/cart" onClick={() => setMobileMenuOpen(false)} className="block rounded-xl px-2.5 py-2 text-slate-700 hover:bg-purple-50">Cart ({cartCount})</Link>
                </>
              )}
              <div className="mx-2 my-2 rounded-2xl bg-slate-50 px-2.5 py-2">
                <p className="font-medium text-slate-800">{user.name}</p>
                <p className="text-[10px] text-slate-500">{user.role}</p>
              </div>
              <button onClick={handleLogout} className="mt-2 flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-purple-600 to-blue-600 px-3 py-2 text-white">
                <LogOut size={15} /> Logout
              </button>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
};

export default Header;